'use client';
import React, { useState } from 'react';
import { AlertTriangle, MapPin, Clock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

// Backend integration: replace with /api/alerts?acknowledged=false&limit=8
const initialAlerts = [
  {
    id: 'alert-001',
    type: 'geofence',
    severity: 'critical',
    message: 'Unit 14 — Ford F-350 exited authorized zone',
    location: 'Hwy 90, mile marker 42',
    time: '13:51',
    acknowledged: false,
  },
  {
    id: 'alert-002',
    type: 'idle',
    severity: 'warning',
    message: 'Excavator EX-07 idling 48 min — job site unconfirmed',
    location: 'Permian Basin Site C',
    time: '13:44',
    acknowledged: false,
  },
  {
    id: 'alert-003',
    type: 'gps',
    severity: 'critical',
    message: 'GPS signal lost — Unit 22 (Dodge Ram 2500)',
    location: 'Last known: FM 1788, Midland',
    time: '13:38',
    acknowledged: false,
  },
  {
    id: 'alert-004',
    type: 'eta',
    severity: 'warning',
    message: 'Job FJ-2841 ETA 22 min late — client notified',
    location: 'Odessa Industrial Park',
    time: '13:32',
    acknowledged: true,
  },
  {
    id: 'alert-005',
    type: 'speed',
    severity: 'warning',
    message: 'Driver T. Reyes exceeded 80mph on US-385',
    location: 'US-385 N, Eddy County',
    time: '13:18',
    acknowledged: true,
  },
];

const severityConfig = {
  critical: 'border-l-red-500 bg-red-950/20',
  warning: 'border-l-amber-500 bg-amber-950/10',
  info: 'border-l-sky-500 bg-sky-950/10',
};

const severityIcon = {
  critical: <AlertTriangle size={12} className="text-red-400" />,
  warning: <AlertTriangle size={12} className="text-amber-400" />,
  info: <CheckCircle size={12} className="text-sky-400" />,
};

export default function AlertFeed() {
  const [alerts, setAlerts] = useState(initialAlerts);

  const acknowledge = (id: string) => {
    setAlerts((prev) => prev.map((a) => (a.id === id ? { ...a, acknowledged: true } : a)));
    toast.success('Alert acknowledged');
  };

  const unacked = alerts.filter((a) => !a.acknowledged);
  const acked = alerts.filter((a) => a.acknowledged);

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl flex flex-col h-full">
      <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
        <div>
          <h3 className="text-sm font-semibold text-zinc-200">Active Alerts</h3>
          <p className="text-xs text-zinc-500 mt-0.5">{unacked.length} unacknowledged</p>
        </div>
        {unacked.length > 0 && (
          <span className="w-5 h-5 bg-red-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center">
            {unacked.length}
          </span>
        )}
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-thin divide-y divide-zinc-800/50">
        {alerts.map((alert) => (
          <div
            key={alert.id}
            className={`px-4 py-3 border-l-2 transition-opacity ${severityConfig[alert.severity as keyof typeof severityConfig]} ${alert.acknowledged ? 'opacity-40' : 'opacity-100'}`}
          >
            <div className="flex items-start gap-2">
              <div className="mt-0.5">{severityIcon[alert.severity as keyof typeof severityIcon]}</div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-zinc-200 leading-snug">{alert.message}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="flex items-center gap-1 text-[10px] text-zinc-500">
                    <MapPin size={9} /> {alert.location}
                  </span>
                  <span className="flex items-center gap-1 text-[10px] text-zinc-600 font-mono">
                    <Clock size={9} /> {alert.time}
                  </span>
                </div>
              </div>
              {!alert.acknowledged && (
                <button
                  onClick={() => acknowledge(alert.id)}
                  className="text-[10px] text-amber-400 hover:text-amber-300 font-medium whitespace-nowrap ml-1 transition-colors"
                >
                  Ack
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}